import type { AudioConfig, Season, Weather } from "../types";
export type SoundName =
  | "water"
  | "weed"
  | "pest"
  | "fertilize"
  | "prune"
  | "seed"
  | "mulch"
  | "winterCover"
  | "harvest"
  | "remove"
  | "purchase"
  | "reward"
  | "click"
  | "sweepLeaves";
const clamp = (v: number) => Math.max(0, Math.min(1, v));
export class AudioSystem {
  private context: AudioContext | undefined;
  private master?: GainNode;
  private music?: GainNode;
  private ambience?: GainNode;
  private sfx?: GainNode;
  private wind?: { source: AudioBufferSourceNode; gain: GainNode };
  private rain?: { source: AudioBufferSourceNode; gain: GainNode };
  private timer = 0;
  private pianoTimer = 0;
  private config: AudioConfig;
  constructor(config?: Partial<AudioConfig>) {
    this.config = {
      muted: false,
      masterVolume: 0.8,
      musicVolume: 0.28,
      ambienceVolume: 0.4,
      sfxVolume: 0.75,
      ...config,
    };
  }
  get settings(): Readonly<AudioConfig> {
    return this.config;
  }
  async unlock(): Promise<void> {
    this.ensure();
    if (this.context?.state === "suspended") await this.context.resume();
  }
  setMuted(muted: boolean) {
    this.config.muted = muted;
    this.applyVolumes();
  }
  toggleMuted(): boolean {
    this.setMuted(!this.config.muted);
    return this.config.muted;
  }
  setVolumes(values: Partial<Omit<AudioConfig, "muted">>) {
    this.config = { ...this.config, ...values };
    this.applyVolumes();
  }
  update(dt: number, season: Season, weather: Weather, windStrength: number) {
    if (!this.context || this.config.muted) return;
    this.timer += dt;
    this.pianoTimer += dt;
    this.setLayer(
      this.wind?.gain,
      weather === "windy"
        ? 0.32 + windStrength * 0.3
        : 0.08 + windStrength * 0.12,
    );
    this.setLayer(this.rain?.gain, weather === "rainy" ? 0.42 : 0);
    if (this.timer > 3.5) {
      this.timer = 0;
      if (season !== "winter" && weather !== "rainy") this.bird();
    }
    if (this.pianoTimer > 4.5 + Math.random() * 3) {
      this.pianoTimer = 0;
      this.pianoPhrase(season);
    }
  }
  play(name: SoundName) {
    this.ensure();
    if (!this.context || this.config.muted) return;
    switch (name) {
      case "water":
        this.noiseBurst(0.22, 900, 260, "lowpass");
        this.tone(820, 0.08, "sine", 0.07, -280);
        break;
      case "prune":
        this.noiseBurst(0.055, 2400, 950, "highpass");
        setTimeout(() => this.noiseBurst(0.045, 2600, 1100, "highpass"), 65);
        break;
      case "pest":
        this.tone(560, 0.08, "square", 0.06, 500);
        this.tone(1040, 0.14, "sine", 0.045, 600, 0.06);
        break;
      case "fertilize":
      case "reward":
      case "harvest":
        this.sparkle();
        break;
      case "seed":
      case "remove":
      case "mulch":
      case "winterCover":
      case "weed":
      case "sweepLeaves":
        this.noiseBurst(0.09, 1200, 240, "bandpass");
        break;
      case "purchase":
        this.tone(660, 0.08, "sine", 0.05, 220);
        this.tone(990, 0.16, "sine", 0.04, 180, 0.08);
        break;
      default:
        this.tone(420, 0.045, "sine", 0.025, 40);
    }
  }
  dispose() {
    if (this.context) this.context.close().catch(() => undefined);
    this.context = undefined;
  }
  private ensure() {
    if (this.context) return;
    const C = window.AudioContext ?? window.webkitAudioContext;
    if (!C) return;
    const c = new C();
    this.context = c;
    this.master = c.createGain();
    this.music = c.createGain();
    this.ambience = c.createGain();
    this.sfx = c.createGain();
    this.music.connect(this.master);
    this.ambience.connect(this.master);
    this.sfx.connect(this.master);
    this.master.connect(c.destination);
    this.wind = this.loopNoise("lowpass", 500);
    this.rain = this.loopNoise("highpass", 1800);
    this.applyVolumes();
  }
  private applyVolumes() {
    if (!this.context) return;
    const now = this.context.currentTime;
    this.master?.gain.setTargetAtTime(
      this.config.muted ? 0 : clamp(this.config.masterVolume),
      now,
      0.03,
    );
    this.music?.gain.setTargetAtTime(clamp(this.config.musicVolume), now, 0.03);
    this.ambience?.gain.setTargetAtTime(
      clamp(this.config.ambienceVolume),
      now,
      0.03,
    );
    this.sfx?.gain.setTargetAtTime(clamp(this.config.sfxVolume), now, 0.03);
  }
  private loopNoise(type: BiquadFilterType, frequency: number) {
    const c = this.context as AudioContext,
      b = c.createBuffer(1, c.sampleRate * 2, c.sampleRate),
      d = b.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * 0.5;
    const source = c.createBufferSource(),
      filter = c.createBiquadFilter(),
      gain = c.createGain();
    filter.type = type;
    filter.frequency.value = frequency;
    gain.gain.value = 0;
    source.buffer = b;
    source.loop = true;
    source
      .connect(filter)
      .connect(gain)
      .connect(this.ambience as GainNode);
    source.start();
    return { source, gain };
  }
  private setLayer(gain: GainNode | undefined, value: number) {
    if (this.context && gain)
      gain.gain.setTargetAtTime(value, this.context.currentTime, 0.8);
  }
  private tone(
    freq: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    bend = 0,
    delay = 0,
    destination?: AudioNode,
  ) {
    if (!this.context) return;
    const t = this.context.currentTime + delay,
      o = this.context.createOscillator(),
      g = this.context.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    o.frequency.exponentialRampToValueAtTime(
      Math.max(40, freq + bend),
      t + duration,
    );
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(volume, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + duration);
    o.connect(g).connect(destination ?? (this.sfx as GainNode));
    o.start(t);
    o.stop(t + duration + 0.02);
  }
  private noiseBurst(
    duration: number,
    frequency: number,
    q: number,
    type: BiquadFilterType,
  ) {
    if (!this.context) return;
    const c = this.context,
      b = c.createBuffer(1, Math.ceil(c.sampleRate * duration), c.sampleRate),
      d = b.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    const s = c.createBufferSource(),
      f = c.createBiquadFilter(),
      g = c.createGain();
    f.type = type;
    f.frequency.value = frequency;
    f.Q.value = Math.min(18, q / 100);
    g.gain.setValueAtTime(0.12, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + duration);
    s.buffer = b;
    s.connect(f)
      .connect(g)
      .connect(this.sfx as GainNode);
    s.start();
  }
  private sparkle() {
    [880, 1175, 1568].forEach((n, i) =>
      this.tone(n, 0.32, "sine", 0.045, 160, i * 0.055),
    );
  }
  private bird() {
    const base = 1450 + Math.random() * 700;
    this.tone(base, 0.11, "sine", 0.025, 700, 0, this.ambience);
    this.tone(base * 1.2, 0.09, "sine", 0.018, -250, 0.14, this.ambience);
  }
  private pianoPhrase(season: Season) {
    const roots = { spring: 261.63, summer: 293.66, autumn: 220, winter: 196 },
      root = roots[season];
    [0, 4, 7, 11].forEach((step, i) =>
      this.tone(
        root * Math.pow(2, step / 12),
        1.8,
        "sine",
        0.025,
        -8,
        i * 0.32,
        this.music,
      ),
    );
  }
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}
